import React from "react";
import InstructionCheckbox from "./InstructionCheckbox";
import ImageWithFallback from "../ImageWithFallback";
import { useIsMobile } from "@/hooks/use-mobile";
import { Separator } from "@/components/ui/separator";

interface OrcaInstructionsProps {
  currentPass: number;
  selectedMethod?: "classic" | "yolo";
  newFlowRatio?: string;
  checkedSteps: Record<string, boolean>;
  onStepCheck: (stepId: string, checked: boolean) => void;
  previousFlowRatioOriginal?: string;
}

const OrcaInstructions = ({ 
  currentPass, 
  selectedMethod, 
  newFlowRatio, 
  checkedSteps, 
  onStepCheck,
  previousFlowRatioOriginal
}: OrcaInstructionsProps) => {
  // Get current theme from document
  const getCurrentTheme = () => {
    if (typeof document !== 'undefined') {
      return document.documentElement.getAttribute('data-theme') || 'light';
    }
    return 'light';
  };

  const theme = getCurrentTheme();
  const isMobile = useIsMobile();
  
  const calibrationIcon = theme === "dark" 
    ? "/lovable-uploads/ac64ce35-5a2c-4f3f-8537-ea38897221d4.png"
    : "/lovable-uploads/26ee250b-cd1c-4fcc-a381-939bc72649e1.png";

  const getMethodText = () => {
    if (!selectedMethod) return "Choose your preferred method";
    
    if (selectedMethod === "classic") {
      return currentPass === 1 ? "Click Pass 1" : "Click Pass 2";
    } else {
      return currentPass === 1 ? "Click YOLO (Recommended)" : "Click YOLO (Perfectionist Version)";
    }
  };

  // For Pass 2, display the calculated flow ratio with proper formatting
  const getDisplayFlowRatio = () => {
    if (currentPass === 2 && newFlowRatio) {
      return newFlowRatio;
    }
    return "";
  };

  // Check if flow ratio is within recommended range
  const isFlowRatioInRange = (value: string) => {
    if (!value) return true;
    const num = parseFloat(value.replace(/[,''٫]/g, '.'));
    return !isNaN(num) && num >= 0.8 && num <= 1.2;
  };

  // Get color class for flow ratio value
  const getFlowRatioColorClass = (value: string) => {
    return isFlowRatioInRange(value) 
      ? 'text-green-600 dark:text-green-400' 
      : 'text-red-600 dark:text-red-400';
  };

  return (
    <div className="space-y-4">
      <ol className="space-y-3">
        {currentPass === 2 ? (
          <>
            <InstructionCheckbox 
              id="orca-step1-pass2"
              checked={checkedSteps["orca-step1-pass2"] || false}
              onCheckedChange={(checked) => onStepCheck("orca-step1-pass2", checked)}
            >
              <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Save</span> your new flow ratio{getDisplayFlowRatio() && (
                <span className="ml-1">
                  (<span className={`font-medium ${getFlowRatioColorClass(getDisplayFlowRatio())}`}>
                    {getDisplayFlowRatio()}
                  </span>)
                </span>
              )} in the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>filament settings</span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-500" />
            <InstructionCheckbox 
              id="orca-step2-pass2"
              checked={checkedSteps["orca-step2-pass2"] || false}
              onCheckedChange={(checked) => onStepCheck("orca-step2-pass2", checked)}
            >
              Click <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>File</span> → <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>New Project</span> to reset the slicer settings
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-500" />
            <InstructionCheckbox 
              id="orca-step3-pass2"
              checked={checkedSteps["orca-step3-pass2"] || false}
              onCheckedChange={(checked) => onStepCheck("orca-step3-pass2", checked)}
            >
              <span>
                Click the
                <ImageWithFallback
                  src={calibrationIcon}
                  alt="calibration menu" 
                  className="h-4 w-4 inline mx-1"
                  fallbackSrc="/lovable-uploads/969ab308-c393-401f-9eb6-1482aee779d4.png"
                />
                <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Calibration menu</span> in the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>top left corner</span>
              </span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-500" />
            <InstructionCheckbox 
              id="orca-step4-pass2"
              checked={checkedSteps["orca-step4-pass2"] || false}
              onCheckedChange={(checked) => onStepCheck("orca-step4-pass2", checked)}
            >
              Click <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Flow Rate → {selectedMethod === "yolo" ? "YOLO (Perfectionist Version)" : "(Pass 2)"}</span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-500" />
            <InstructionCheckbox 
              id="orca-step5-pass2"
              checked={checkedSteps["orca-step5-pass2"] || false}
              onCheckedChange={(checked) => onStepCheck("orca-step5-pass2", checked)}
            >
              <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Slice</span> and <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>print</span> the test objects
            </InstructionCheckbox>
          </>
        ) : (
          <>
            <InstructionCheckbox 
              id="orca-step1"
              checked={checkedSteps["orca-step1"] || false}
              onCheckedChange={(checked) => onStepCheck("orca-step1", checked)}
            >
              <span>
                Click the
                <ImageWithFallback
                  src={calibrationIcon}
                  alt="calibration menu" 
                  className="h-4 w-4 inline mx-1"
                  fallbackSrc="/lovable-uploads/969ab308-c393-401f-9eb6-1482aee779d4.png"
                />
                <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Calibration menu</span> in the <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>top left corner</span>
              </span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-300" />
            <InstructionCheckbox 
              id="orca-step2"
              checked={checkedSteps["orca-step2"] || false}
              onCheckedChange={(checked) => onStepCheck("orca-step2", checked)}
            >
              Click <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Flow Rate → {selectedMethod === "yolo" ? "YOLO (Recommended)" : "Pass 1"}</span>
            </InstructionCheckbox>
            <Separator className="my-3 bg-gray-300" />
            <InstructionCheckbox 
              id="orca-step4"
              checked={checkedSteps["orca-step4"] || false}
              onCheckedChange={(checked) => onStepCheck("orca-step4", checked)}
            >
              <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>Slice</span> and <span className={`px-1 rounded ${theme === 'dark' ? 'bg-blue-950 text-blue-200' : 'bg-blue-100 text-blue-800'}`}>print</span> the calibration objects
            </InstructionCheckbox>
          </>
        )}
      </ol>
      
      <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
        {currentPass === 1 ? (
          <>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Tip:</strong> You can usually get away with removing the highest and lowest values to save time and filament.
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
              Check all boxes to continue to the next step
            </p>
          </>
        ) : (
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Check all boxes to continue to the next step
          </p>
        )}
      </div>
    </div>
  );
};

export default OrcaInstructions;
